import React, {useRef, forwardRef, useEffect, useState, useCallback} from 'react';
import { isAwaitingProcessing } from '../../utils/articleReadiness';
import { useDrag, useDrop } from 'react-dnd';
import { countOf, formatDate, getArticlePublisherName, getLanguageName, getSourceTitle2, timeAgo } from './transformers';
import {api} from "../../app/http-common";
import { useIsAdmin, useCanEdit, useHasFeature } from '../../hooks/useFrontegg';
import { STORY_BUCKET_TYPE } from './BucketSidebar';

const ITEM_TYPE = 'ARTICLE';

// Helper to find the best source article URL with priority:
// 1. First preferred publisher article URL
// 2. First English article URL
// 3. First article URL
const getBestSourceUrl = (articles) => {
    if (!articles || articles.length === 0) return '';
    const preferred = articles.find(a => a.isPreferredPublisher && a.url);
    if (preferred) return preferred.url;
    const english = articles.find(a => (a.lang === 'eng' || a.lang === 'en' || !a.lang) && a.url);
    if (english) return english.url;
    const first = articles.find(a => a.url);
    return first?.url || '';
};

// Helper to extract text from excerpt - handles both plain text and JSON array formats
const getExcerptText = (excerpt) => {
    if (!excerpt?.text) return null;
    let text = excerpt.text;
    
    if (typeof text !== 'string') return text;
    
    // Trim the text
    text = text.trim();
    
    // Remove outer quotes if the whole string is quoted
    if ((text.startsWith('"') && text.endsWith('"')) ||
        (text.startsWith("'") && text.endsWith("'"))) {
        text = text.slice(1, -1);
    }
    
    // Check if it looks like JSON (starts with '[' or '{')
    if (text.startsWith('[') || text.startsWith('{')) {
        try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed) && parsed.length > 0) {
                // Extract translations from the array and join them
                const extracted = parsed.map(item => 
                    item.translation || item.text || item.original_text || 
                    (typeof item === 'string' ? item : '')
                ).filter(Boolean).join(' ');
                if (extracted) return extracted;
            } else if (typeof parsed === 'object' && parsed !== null) {
                // Handle object format: {"translation": "..."}
                const extracted = parsed.translation || parsed.text || parsed.original_text;
                if (extracted) return extracted;
            }
        } catch (e) {
            // Not valid JSON, return as-is
        }
    }
    
    return text;
};

// Skeleton loader component for preview content
const SkeletonLoader = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{
            height: '14px',
            width: '100%',
            backgroundColor: '#e0e0e0',
            borderRadius: '4px',
            animation: 'shimmer 1.5s ease-in-out infinite',
            background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
            backgroundSize: '200% 100%'
        }} />
        <div style={{
            height: '14px',
            width: '92%',
            backgroundColor: '#e0e0e0',
            borderRadius: '4px',
            animation: 'shimmer 1.5s ease-in-out infinite',
            animationDelay: '0.1s',
            background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
            backgroundSize: '200% 100%'
        }} />
        <div style={{
            height: '14px',
            width: '75%',
            backgroundColor: '#e0e0e0',
            borderRadius: '4px',
            animation: 'shimmer 1.5s ease-in-out infinite',
            animationDelay: '0.2s',
            background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
            backgroundSize: '200% 100%'
        }} />
        <style>{`
            @keyframes shimmer {
                0% { background-position: 200% 0; }
                100% { background-position: -200% 0; }
            }
        `}</style>
    </div>
);

// Copy icon SVG component
const CopyIcon = ({ size = 14 }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
    </svg>
);

const ShareIcon = ({ size = 14 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="18" cy="5" r="3"/>
        <circle cx="6" cy="12" r="3"/>
        <circle cx="18" cy="19" r="3"/>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
);

// Chevron icons for collapse/expand
const ChevronRight = ({ size = 16 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

const ChevronDown = ({ size = 16 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

// Checkmark icon for Mark as Completed
const CheckIcon = ({ size = 12 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

// Merge icon for Select for Merge
const MergeIcon = ({ size = 12 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M6 3v12" />
        <path d="M18 9a3 3 0 100-6 3 3 0 000 6z" />
        <path d="M6 21a3 3 0 100-6 3 3 0 000 6z" />
        <path d="M15 6c-6 0-6 12 0 12" />
    </svg>
);

const Article = forwardRef(({
                                article,
                                index,
                                moveArticle,
                                isSelected,
                                toggleSelection,
                                formatDate,
                                isDraggable,
                                hoverable,
                                isHovered,
                                setHoveredIndex,
                                isMergeSelected,
                                toggleMergeSelection,
                                animationKey,
                                type,
                                alreadyDone,
                                prs,
                                showToastNotification,
                                expandAllTrigger,
                                isHighlighted,
                            }, ref) => {
    const internalRef = useRef(null);
    const isAdmin = useIsAdmin();
    const _canEdit = useCanEdit();
    const canEdit = (new URLSearchParams(window.location.search).get('designFixture') === '1') || _canEdit;
    const { isEntitled: hasSocialPosts } = useHasFeature('social-posts-creation');
    const [localAlreadyDone, setLocalAlreadyDone] = useState(false);
    const [isHidden, setIsHidden] = useState(article?.hidden || false); // Track the hidden status locally
    const [isExpanded, setIsExpanded] = useState(false); // Track excerpt expansion
    const [translatedExcerpt, setTranslatedExcerpt] = useState(null); // Translated excerpt text
    const [isCollapsed, setIsCollapsed] = useState(type === 'story'); // Stories start collapsed
    const [isTranslating, setIsTranslating] = useState(false); // Translation loading state
    const [generatingSocialPost, setGeneratingSocialPost] = useState(false); // Social post generation loading
    const storyCardArticleCount = type === 'story'
        ? (article?.totalArticleCount ?? article?.articleCount ?? article?.articles?.length ?? 0)
        : (article?.articles?.length ?? 0);
    const shouldShowStoryCardArticleCount = Number.isFinite(storyCardArticleCount) && storyCardArticleCount > 0;

    useEffect(() => {
        setLocalAlreadyDone(alreadyDone);
        setIsHidden(article?.hidden || false); // Initialize the local hidden state
    }, [alreadyDone, article?.hidden]);

    // Respond to expand all trigger - expand if currently collapsed
    useEffect(() => {
        if (expandAllTrigger > 0 && type === 'story' && isCollapsed) {
            setIsCollapsed(false);
        }
    }, [expandAllTrigger, type]);

    // Handle translation when excerpt is expanded and not in English
    const handleExpandClick = useCallback(async (e) => {
        e.stopPropagation();
        
        const excerptText = getExcerptText(article.excerpt);
        const isEnglish = article.excerpt?.isEnglish;
        
        if (!isExpanded && excerptText && !isEnglish && !translatedExcerpt) {
            // Expanding for the first time with non-English text - translate it
            setIsExpanded(true);
            setIsTranslating(true);
            try {
                // Use fast AI-based translation (Gemini) to avoid Google Translate rate limits
                const result = await api.translateFast({ texts: [excerptText], target: 'en' });
                // Result could be array or object with translations property
                const translations = Array.isArray(result) ? result : result?.translations;
                if (translations && translations.length > 0) {
                    let translated = translations[0];
                    // Clean up if translation is in JSON format
                    if (typeof translated === 'string') {
                        translated = translated.trim();
                        // Remove outer quotes
                        if ((translated.startsWith('"') && translated.endsWith('"')) ||
                            (translated.startsWith("'") && translated.endsWith("'"))) {
                            translated = translated.slice(1, -1);
                        }
                        // Parse JSON if needed
                        if (translated.startsWith('[') || translated.startsWith('{')) {
                            try {
                                const parsed = JSON.parse(translated);
                                if (Array.isArray(parsed) && parsed.length > 0) {
                                    translated = parsed.map(item => 
                                        item.translation || item.text || item.original_text || 
                                        (typeof item === 'string' ? item : '')
                                    ).filter(Boolean).join(' ');
                                } else if (typeof parsed === 'object' && parsed !== null) {
                                    translated = parsed.translation || parsed.text || parsed.original_text || translated;
                                }
                            } catch (e) {
                                // Not JSON, use as-is
                            }
                        }
                    }
                    setTranslatedExcerpt(translated);
                } else {
                    // If translation failed or empty, show original text
                    setTranslatedExcerpt(excerptText);
                }
            } catch (err) {
                if (err?.response?.status !== 403) {
                    console.error('Translation error:', err);
                }
                setTranslatedExcerpt(excerptText);
            } finally {
                setIsTranslating(false);
            }
        } else {
            setIsExpanded(!isExpanded);
        }
    }, [isExpanded, article.excerpt, translatedExcerpt]);

    // DnD hooks
    const [, drop] = useDrop({
        accept: ITEM_TYPE,
        hover(item) {
            if (item.index !== index) {
                moveArticle(item.index, index);
                item.index = index;
            }
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type: ITEM_TYPE,
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    // Bucket drag source — stories can be dragged into publisher buckets
    const [, bucketDrag] = useDrag({
        type: STORY_BUCKET_TYPE,
        item: { storyId: article._id, title: article.title },
        canDrag: () => type === 'story',
    });

    // Apply drag and drop refs
    if (isDraggable) {
        drag(drop(internalRef));
    } else if (type === 'story') {
        bucketDrag(drop(internalRef));
    } else {
        drop(internalRef);
    }

    // Combine the forwarded ref with the internal ref
    const combinedRef = (node) => {
        internalRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    // Styling constants
    const color = type === 'story' ? '#FF6B35' : type === 'article' ? '#FF6B35' : '#FFDAB9';
    const frameColor = 'gray';
    const completedColor = '#FFD7A8'; // Light orange
    const uncompletedColor = '#f5f5f5';
    const backgroundColor = localAlreadyDone? completedColor :uncompletedColor;


    const tagStyle = {
        display: 'inline-block',
        padding: '4px 10px',
        margin: '3px 6px 3px 0',
        background: '#FF6B35',
        borderRadius: '16px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#ffffff',
        whiteSpace: 'nowrap',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        boxShadow: 'none',
        transition: 'all 0.2s ease',
    };

    const languageTagStyle = {
        display: 'inline-block',
        padding: '3px 8px',
        margin: '2px 4px 2px 0',
        background: 'rgba(60,74,94,0.06)',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '400',
        color: '#3C4A5E',
        whiteSpace: 'nowrap',
        letterSpacing: '0.2px',
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        border: '1px solid rgba(60,74,94,0.12)',
    };

    // Simplified language style for expanded view (no button-like appearance)
    const languageTextStyle = {
        display: 'inline',
        fontSize: '11px',
        fontWeight: '500',
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
    };

    const metricTagStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        margin: '3px 6px 3px 0',
        background: '#f0f7ff',
        borderRadius: '16px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#0066cc',
        whiteSpace: 'nowrap',
        letterSpacing: '0.3px',
        border: '1px solid #cce0ff',
    };

    const getSentimentLabel = (sentiment) => {
        if (sentiment == null) return { label: 'Unknown', color: '#9ca3af', bg: '#f3f4f6', border: '#d1d5db' };
        if (sentiment >= 0.6) return { label: 'Positive', color: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0' };
        if (sentiment <= 0.4) return { label: 'Negative', color: '#ef4444', bg: '#fef2f2', border: '#fecaca' };
        return { label: 'Neutral', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' };
    };

    const getRankingLabel = (ranking) => {
        if (ranking == null) return null;
        if (ranking >= 80) return { label: 'Very High', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' };
        if (ranking >= 60) return { label: 'High', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' };
        if (ranking >= 40) return { label: 'Medium', color: '#ca8a04', bg: '#fefce8', border: '#fef08a' };
        return { label: 'Low', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' };
    };

    const formatShares = (totalShares) => {
        if (!totalShares) return null;
        const total = (totalShares.facebook || 0) + (totalShares.twitter || 0) + (totalShares.linkedin || 0);
        if (total === 0) return null;
        if (total >= 1000000) return `${(total / 1000000).toFixed(1)}M`;
        if (total >= 1000) return `${(total / 1000).toFixed(1)}K`;
        return total.toString();
    };

    // Helper to get feedSource tag styling and label
    const getFeedSourceTag = (feedSource) => {
        if (!feedSource) return null;
        if (feedSource === 'newsapi_expansion') {
            return {
                label: '📁 CAT',
                style: {
                    display: 'inline-block',
                    padding: '2px 6px',
                    margin: '0 6px 0 0',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: '4px',
                    fontSize: '9px',
                    fontWeight: '700',
                    color: '#ffffff',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    verticalAlign: 'middle',
                }
            };
        }
        if (feedSource === 'newsapi_expansion_concepts') {
            return {
                label: '💡 CONCEPT',
                style: {
                    display: 'inline-block',
                    padding: '2px 6px',
                    margin: '0 6px 0 0',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                    borderRadius: '4px',
                    fontSize: '9px',
                    fontWeight: '700',
                    color: '#ffffff',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    verticalAlign: 'middle',
                }
            };
        }
        if (feedSource === 'newsapi_feed_pr') {
            return {
                label: 'PR',
                style: {
                    display: 'inline-block',
                    padding: '2px 6px',
                    margin: '0 6px 0 0',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    borderRadius: '4px',
                    fontSize: '9px',
                    fontWeight: '700',
                    color: '#ffffff',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    verticalAlign: 'middle',
                }
            };
        }
        return null;
    };

    // Helper to format feed sources array for story display
    const formatFeedSources = (feedSources) => {
        if (!feedSources || feedSources.length === 0) return null;
        const labels = feedSources.map(source => {
            if (source === 'newsapi_preferred') return '⭐ Preferred';
            if (source === 'newsapi_expansion') return '📁 Category';
            if (source === 'newsapi_expansion_concepts') return '💡 Concept';
            if (source === 'newsapi_feed_pr') return 'PR';
            if (source === 'newsapi_feed') return 'NewsAPI';
            return source;
        });
        return labels.join(', ');
    };

    const setStoryCompleted = async (_storyId,completed) => {
        await api.setStoryCompleted({storyId:_storyId,completed});
    };

    const hideArticle = (articleId) => {
        setIsHidden(true);
        api.hideArticle(articleId).catch(() => setIsHidden(false));
    };

    // Early return if the article is hidden
    if (isHidden) {
        return null;
    }

    // §4 — an article outside the feed's scope stays readable and openable; only
    // its emphasis drops. Opacity rather than a colour change, so it degrades the
    // whole card uniformly without inventing a palette entry. `extra` and
    // `outsideTimeRange` both demote, but they are labelled apart below: only the
    // first is a statement about a filter the reader chose.
    const nonMatching = Boolean(article?.extra || article?.outsideTimeRange);

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px', opacity: nonMatching ? 0.62 : 1 }}>
            {/* Checkbox for non-story articles positioned outside the rectangle */}
            {article?.type !== 'story' && (
                <input
                    type="checkbox"
                    // `lockedOutOfScope` — restricted production may only use
                    // articles matching the suggestion's saved scope. Rendering
                    // these unchecked AND disabled is the point: a disabled
                    // control that still shows a tick reads as "selected", which
                    // is exactly what it is not.
                    disabled={Boolean(article?.lockedOutOfScope)}
                    title={article?.lockedOutOfScope
                        ? 'Outside this draft\u2019s source scope — readable, but not usable as a source'
                        : undefined}
                    checked={(article?.hidden || article?.lockedOutOfScope) ? false : isSelected}
                    onChange={() => {
                        if (!article?.hidden && !article?.lockedOutOfScope) {
                            toggleSelection(article, index); // Only toggle selection if the article is not hidden
                        }
                    }}
                    style={{
                        marginRight: '10px',
                        marginTop: '18px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#FF6B35',
                        cursor: article?.lockedOutOfScope ? 'not-allowed' : 'pointer',
                        flexShrink: 0,
                    }}
                />
            )}

            <div
                ref={combinedRef}
                onClick={() => {
                    if (type === 'article' && article.url) {
                        window.open(article.url, '_blank', 'noopener,noreferrer');
                    } else {
                        toggleSelection(article, index);
                    }
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(-1)}

                style={{
                    cursor: 'pointer',
                    padding: '8px 10px',
                    marginTop: '4px',
                    width: '100%',
                    boxShadow: (isHovered && hoverable) || isSelected 
                        ? '0 8px 32px rgba(255,107,53,0.20)' 
                        : isHighlighted
                            ? '0 4px 20px rgba(255,107,53,0.30)'
                            : '0 2px 10px rgba(0,0,0,0.06)',
                    background: localAlreadyDone 
                        ? 'linear-gradient(135deg, #fff9f5 0%, #ffe8d6 100%)' 
                        : isHighlighted
                            ? 'linear-gradient(135deg, #fff8f0 0%, #fff3e6 100%)'
                            : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    borderRadius: '12px',
                    border: (isHovered && hoverable) || isSelected 
                        ? '2px solid #FF6B35'
                        : isHighlighted
                            ? '2px solid #FF6B35'
                            : '2px solid rgba(0,0,0,0.06)',
                    borderLeft: (isHovered && hoverable) || isSelected 
                        ? `6px solid ${color}` 
                        : isHighlighted
                            ? '6px solid #FF6B35'
                            : '6px solid #e0e0e0',
                    opacity: isDragging ? 0.5 : 1,
                    userSelect: 'none',
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(10px)',
                }}
            >
                <div
                    className={index === 0 && animationKey ? 'article-content-splash-story' : 'article-content'}
                    key={animationKey}
                >
                    {/* Collapsed view for stories - shows minimal info */}
                    {type === 'story' && isCollapsed && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            animation: 'fadeIn 0.2s ease',
                        }}>
                            {/* Expand/Collapse toggle */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsCollapsed(false);
                                }}
                                style={{
                                    background: '#f5f7fa',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    padding: '4px 6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#555',
                                    flexShrink: 0,
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#e8ecf1';
                                    e.currentTarget.style.borderColor = '#bbb';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#f5f7fa';
                                    e.currentTarget.style.borderColor = '#ddd';
                                }}
                                title="Expand"
                            >
                                <ChevronRight size={14} />
                            </button>
                            {/* Title - click to expand */}
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsCollapsed(false);
                                }}
                                style={{
                                    cursor: 'pointer',
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                <div style={{
                                    fontWeight: '400',
                                    fontSize: '16px',
                                    lineHeight: '1.4',
                                    color: '#0B1E3F',
                                    letterSpacing: '-0.2px',
                                }}>
                                    {getFeedSourceTag(article.feedSource) && (
                                        <span style={getFeedSourceTag(article.feedSource).style}>
                                            {getFeedSourceTag(article.feedSource).label}
                                        </span>
                                    )}
                                    {article.subject || article.title}
                                </div>
                            </div>
                            {/* Copy button (icon only) */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const firstEnglishArticle = article.articles?.find(a => a.lang === 'eng') || article.articles?.[0];
                                    const storyUrl = article.slug
                                        ? `${window.location.origin}/s/${article.slug}`
                                        : firstEnglishArticle?.url || '';
                                    const storyTitle = article.title || article.subject || '';
                                    const sourceUrl = getBestSourceUrl(article.articles);
                                    const textToCopy = `${storyTitle}\n${storyUrl}${sourceUrl ? `\n${sourceUrl}` : ''}`;
                                    navigator.clipboard.writeText(textToCopy)
                                        .then(() => {
                                            if (showToastNotification) {
                                                showToastNotification('Link copied to clipboard!');
                                            }
                                        })
                                        .catch(err => console.error('Failed to copy:', err));
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    color: '#3C4A5E',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                                title="Share public story"
                            >
                                <ShareIcon size={14} />
                            </button>
                            {/* Article count indicator */}
                            {shouldShowStoryCardArticleCount && (
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#888',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '2px',
                                }}>
                                    {storyCardArticleCount} 📄
                                </div>
                            )}
                        </div>
                    )}

                    {/* Expanded view - original content */}
                    {(type !== 'story' || !isCollapsed) && (
                    <div style={{ animation: 'expandContent 0.2s ease' }}>
                    {/* Collapse toggle for expanded stories */}
                    {type === 'story' && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsCollapsed(true);
                            }}
                            style={{
                                background: '#f5f7fa',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                padding: '4px 6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#555',
                                marginRight: '8px',
                                verticalAlign: 'middle',
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#e8ecf1';
                                e.currentTarget.style.borderColor = '#bbb';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#f5f7fa';
                                e.currentTarget.style.borderColor = '#ddd';
                            }}
                            title="Collapse"
                        >
                            <ChevronDown size={14} />
                        </button>
                    )}
                    {type === 'claim' ? (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%' }}>
                            <div style={{
                                fontStyle: 'italic',
                                fontSize: '14px',
                                fontWeight: '400',
                                lineHeight: '1.5',
                                color: '#0B1E3F',
                                marginBottom: '4px',
                                flex: 1,
                                minWidth: 0,
                            }}>{article.text || article.claim || article.title}</div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const text = article.text || article.claim || article.title;
                                    navigator.clipboard.writeText(text).then(() => {
                                        if (showToastNotification) showToastNotification('Claim copied!');
                                    });
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '2px 8px 2px 2px',
                                    color: '#00AEEF',
                                    flexShrink: 0,
                                    marginTop: '2px',
                                }}
                                title="Copy claim"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                                </svg>
                            </button>
                        </div>
                    ) : (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <div style={{
                            fontWeight: '400',
                            fontSize: '16px',
                            lineHeight: '1.4',
                            color: '#0B1E3F',
                            marginBottom: '4px',
                            letterSpacing: '-0.2px',
                            display: type === 'story' ? 'inline' : 'block',
                            flex: 1,
                            minWidth: 0,
                        }}>
                            {article.subject || article.title}
                        </div>
                        {type === 'article' && article.url && (
                            <a
                                href={article.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    color: '#00AEEF',
                                    flexShrink: 0,
                                    marginTop: '2px',
                                    paddingRight: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                                title="Open original article"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                                    <polyline points="15 3 21 3 21 9"/>
                                    <line x1="10" y1="14" x2="21" y2="3"/>
                                </svg>
                            </a>
                        )}
                    </div>
                    )}
                    {/* For stories, show feed source tag on separate line */}
                    {type === 'story' && getFeedSourceTag(article.feedSource) && (
                        <div style={{ marginBottom: '4px' }}>
                            <span style={getFeedSourceTag(article.feedSource).style}>
                                {getFeedSourceTag(article.feedSource).label}
                            </span>
                        </div>
                    )}
                    <div className="article-info">
                        {/* Compact metadata line for articles - all info in one line, regular text */}
                        {type === 'article' && (
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: '6px',
                                marginTop: '4px',
                                fontSize: '11px',
                                color: '#3C4A5E'
                            }}>
                                {/* Publisher name, resolved server-side so this
                                    row and the bucket screen agree. */}
                                {(article.publisherName || article.sources?.length > 0 || article.source) && (
                                    <span style={{ fontWeight: '500' }}>
                                        {article.isPreferredPublisher && (
                                            <span title="Preferred Publisher" style={{ marginRight: '3px', filter: 'grayscale(1)', opacity: 0.6 }}>⭐</span>
                                        )}
                                        {getArticlePublisherName(article)}
                                    </span>
                                )}
                                {/* Feed source type (Category, Concept) - regular text */}
                                {article.feedSource === 'newsapi_expansion' && (
                                    <>
                                        <span style={{ color: '#ccc' }}>|</span>
                                        <span>Category</span>
                                    </>
                                )}
                                {article.feedSource === 'newsapi_expansion_concepts' && (
                                    <>
                                        <span style={{ color: '#ccc' }}>|</span>
                                        <span>Concept</span>
                                    </>
                                )}
                                {/* Language */}
                                {article.lang && (
                                    <>
                                        <span style={{ color: '#ccc' }}>|</span>
                                        <span>{getLanguageName(article.lang)}</span>
                                    </>
                                )}
                                {/* PR Source */}
                                {prs && article.prSource && (
                                    <>
                                        <span style={{ color: '#ccc' }}>|</span>
                                        <span>{article.prSource}</span>
                                    </>
                                )}
                                {/* Time */}
                                {article.dateTimePub && (
                                    <>
                                        <span style={{ color: '#ccc' }}>|</span>
                                        <span><span style={{ filter: 'grayscale(1)', opacity: 0.6 }}>🕐</span> {timeAgo(article.dateTimePub)}</span>
                                    </>
                                )}
                                {/* Did not match the filter the story was opened under, the
                                    saved scope of the suggestion it was opened from, or simply
                                    has not finished processing. The last is NOT a statement
                                    about the filter and must not be labelled as one. */}
                                {(article?.extra || article?.outsideTimeRange || article?.lockedOutOfScope || isAwaitingProcessing(article)) && (
                                    <span style={{
                                        background: 'rgba(60,74,94,0.10)',
                                        color: '#3C4A5E',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        fontSize: '10px',
                                        marginLeft: '4px'
                                    }}>
                                        {isAwaitingProcessing(article)
                                            ? 'Still processing'
                                            : article?.lockedOutOfScope
                                                ? 'Outside the draft scope'
                                                : article?.outsideTimeRange
                                                    ? 'Outside the selected time range'
                                                    : 'Outside this filter'}
                                    </span>
                                )}
                            </div>
                        )}
                        {/* Claim extraction metadata */}
                        {article?.type !== 'story' && article?.articleCount && type !== 'article' && (
                            <div style={{ fontSize: '13px', color: '#3C4A5E', marginTop: '2px' }}>
                                Extracted from <strong>{article.articleCount}</strong>{' '}
                                {article.articleCount === 1 ? 'article' : 'articles'}
                            </div>
                        )}
                        {article?.type === 'story' && (
                            <div
                                className="publication-date-wrapper"
                                style={{
                                    color: '#888',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                    fontSize: '11px',
                                    marginTop: '2px',
                                }}
                            >
                                {article.languages &&
                                    article.languages.split(',').map((language, idx, arr) => (
                                        <span key={idx} style={languageTextStyle}>
                                            {language}{idx < arr.length - 1 ? ', ' : ''}
                                        </span>
                                    ))}
                            </div>
                        )}
                        {type === 'claim' && article.sources && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                                {article.sources.map((source, idx) => (
                                    <span key={idx} style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        color: '#3C4A5E',
                                        background: '#F0F2F5',
                                        borderRadius: '10px',
                                        border: '1px solid #E0E4E8',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {getSourceTitle2(source)}
                                    </span>
                                ))}
                            </div>
                        )}
                        {type !== 'article' && type !== 'claim' && article.sources &&
                            article.sources.map((source, idx) => (
                                <span key={idx} style={languageTagStyle}>
                                    {article.isPreferredPublisher && idx === 0 && (
                                        <span title="Preferred Publisher" style={{ marginRight: '3px', filter: 'grayscale(1)', opacity: 0.6 }}>⭐</span>
                                    )}
                                    {getSourceTitle2(source)}
                                </span>
                            ))}
                        {type !== 'article' && prs && article.prSource && (
                            <span style={languageTagStyle}>
                                    {article.prSource}
                                </span>)
                        }

                        {article.dateTimePub && type !== 'article' && type !== 'story' && (
                            <div className="publication-date" style={{color: 'gray'}}>
                                {timeAgo(article.dateTimePub)}
                                {(article?.extra || article?.outsideTimeRange || article?.lockedOutOfScope || isAwaitingProcessing(article)) && (
                                    <div className="publication-date" style={{
                                        ...tagStyle,
                                        backgroundColor: 'rgba(60,74,94,0.10)',
                                        color: '#3C4A5E',
                                        margin: '7px',

                                    }}>
                                        {isAwaitingProcessing(article)
                                            ? 'Still processing'
                                            : article?.lockedOutOfScope
                                                ? 'Outside the draft scope'
                                                : article?.outsideTimeRange
                                                    ? 'Outside the selected time range'
                                                    : 'Outside this filter'}
                                    </div>)}
                            </div>
                        )}

                        {type !== 'article' && article.lang && (
                            <div className="publication-date" style={tagStyle}>
                                {getLanguageName(article.lang)}
                            </div>
                        )}
                    </div>
                    
                    {/* Slim metadata line - all metrics in one row */}
                    {type === 'story' && (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '6px',
                            marginTop: '4px',
                            padding: '4px 0',
                            borderTop: '1px dashed #e5e5e5',
                            fontSize: '11px',
                            color: '#3C4A5E'
                        }}>
                            {/* Ranking */}
                            {article.maxRanking != null && (() => {
                                const rankInfo = getRankingLabel(article.maxRanking);
                                return (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <span style={{ color: rankInfo.color, fontWeight: '600' }}>📊 {rankInfo.label}</span>
                                    </span>
                                );
                            })()}
                            {/* Sentiment */}
                            {article.dominantSentiment != null && (() => {
                                const sentimentInfo = getSentimentLabel(article.dominantSentiment);
                                const emoji = article.dominantSentiment >= 0.6 ? '😊' : 
                                              article.dominantSentiment <= 0.4 ? '😟' : '😐';
                                return (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <span>{emoji}</span>
                                        <span style={{ color: sentimentInfo.color }}>{sentimentInfo.label}</span>
                                    </span>
                                );
                            })()}
                            {/* Feed Sources - separator and source label */}
                            {article.feedSources && formatFeedSources(article.feedSources) && (
                                <>
                                    <span style={{ color: '#ccc' }}>|</span>
                                    <span style={{ color: '#3C4A5E' }}>📡 {formatFeedSources(article.feedSources)}</span>
                                </>
                            )}
                            {/* Shares */}
                            {formatShares(article.totalShares) && (
                                <span>📤 {formatShares(article.totalShares)}</span>
                            )}
                            {/* Separator */}
                            {article?.articles?.length && <span style={{ color: '#ccc' }}>|</span>}
                            {/* Dates */}
                            {article?.articles?.length && (
                                <span>
                                    🕐 Updated {timeAgo(article.recent)} · First {timeAgo(article.earliest)}
                                </span>
                            )}
                        </div>
                    )}
                    
                    {/* Action buttons row with article count on right */}
                    {type === 'story' && (
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px', gap: '8px'}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0}}>
                                {/* Show/Hide Preview button - first */}
                                {article.excerpt && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleExpandClick(e);
                                        }}
                                        style={{
                                            padding: '5px 12px',
                                            background: 'none',
                                            color: '#3C4A5E',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                            transition: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                        }}
                                    >
                                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                        {isExpanded ? 'Hide Preview' : 'Show Preview'}
                                    </button>
                                )}
                                {/* Mark as completed - requires edit permission */}
                                {canEdit && (<button
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        const newStatus = !localAlreadyDone;
                                        await setStoryCompleted(article._id, newStatus);
                                        setLocalAlreadyDone(newStatus);
                                    }}
                                    style={{
                                        padding: '5px 12px',
                                        background: localAlreadyDone
                                            ? '#FF6B35' 
                                            : 'none',
                                        color: localAlreadyDone ? '#ffffff' : '#3C4A5E',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        boxShadow: localAlreadyDone 
                                            ? '0 3px 10px rgba(255, 153, 51, 0.4)' 
                                            : '0 2px 6px rgba(0,0,0,0.1)',
                                        transition: 'none',
                                        textTransform: 'capitalize',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <CheckIcon size={12} />
                                    {localAlreadyDone ? 'Mark as uncompleted' : 'Mark as completed'}
                                </button>)}
                                {canEdit && toggleMergeSelection && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleMergeSelection(article);
                                        }}
                                        style={{
                                            padding: '5px 12px',
                                            background: isMergeSelected 
                                                ? '#FF6B35' 
                                                : 'none',
                                            color: isMergeSelected ? '#ffffff' : '#3C4A5E',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            boxShadow: isMergeSelected 
                                                ? '0 3px 10px rgba(102, 126, 234, 0.4)' 
                                                : '0 2px 6px rgba(0,0,0,0.1)',
                                            transition: 'none',
                                            textTransform: 'capitalize',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                        }}
                                    >
                                        <MergeIcon size={12} />
                                        {isMergeSelected ? 'Unselect for Merge' : 'Select for Merge'}
                                    </button>
                                )}
                                {/* Share Public Story button - requires edit permission */}
                                {canEdit && (<button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Find first English article, or fallback to first article
                                        const firstEnglishArticle = article.articles?.find(a => a.lang === 'eng') || article.articles?.[0];
                                        // Build shareable story URL using slug
                                        const storyUrl = article.slug
                                            ? `${window.location.origin}/s/${article.slug}`
                                            : firstEnglishArticle?.url || '';
                                        const storyTitle = article.title || article.subject || '';
                                        const sourceUrl = getBestSourceUrl(article.articles);
                                        const textToCopy = `${storyTitle}\n${storyUrl}${sourceUrl ? `\n${sourceUrl}` : ''}`;
                                        navigator.clipboard.writeText(textToCopy)
                                            .then(() => {
                                                if (showToastNotification) {
                                                    showToastNotification('Link copied to clipboard!');
                                                }
                                            })
                                            .catch(err => {
                                                console.error('Failed to copy:', err);
                                                if (showToastNotification) {
                                                    showToastNotification('Failed to copy link', 'error');
                                                }
                                            });
                                    }}
                                    style={{
                                        padding: '5px 12px',
                                        background: 'none',
                                        color: '#3C4A5E',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                        transition: 'none',
                                        textTransform: 'capitalize',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <ShareIcon size={12} /> Share Public Story
                                </button>)}
                                {/* Copy Internal Link button - requires edit permission */}
                                {canEdit && (<button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const firstEnglishArticle = article.articles?.find(a => a.lang === 'eng') || article.articles?.[0];
                                        const internalUrl = article.slug
                                            ? `${window.location.origin}/story/${article.slug}`
                                            : `${window.location.origin}/story/${article._id}`;
                                        const storyTitle = article.title || article.subject || '';
                                        const sourceUrl = getBestSourceUrl(article.articles);
                                        const textToCopy = `${storyTitle}\n${internalUrl}${sourceUrl ? `\n${sourceUrl}` : ''}`;
                                        navigator.clipboard.writeText(textToCopy)
                                            .then(() => {
                                                if (showToastNotification) {
                                                    showToastNotification('Internal link copied to clipboard!');
                                                }
                                            })
                                            .catch(err => {
                                                console.error('Failed to copy:', err);
                                                if (showToastNotification) {
                                                    showToastNotification('Failed to copy link', 'error');
                                                }
                                            });
                                    }}
                                    style={{
                                        padding: '5px 12px',
                                        background: 'none',
                                        color: '#3C4A5E',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                        transition: 'none',
                                        textTransform: 'capitalize',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <CopyIcon size={12} /> Copy Internal Link
                                </button>)}
                                {/* Create Social Post button - gated by Frontegg entitlement */}
                                {hasSocialPosts && article._id && (
                                    <button
                                        disabled={generatingSocialPost}
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            setGeneratingSocialPost(true);
                                            try {
                                                const data = await api.generateSocialPost({
                                                    storyId: article._id,
                                                    platform: 'instagram',
                                                    language: 'en',
                                                });
                                                if (data?.post?._id) {
                                                    window.open(`/social-posts/${data.post._id}`, '_blank');
                                                }
                                            } catch (err) {
                                                console.error('Failed to generate social post:', err);
                                                if (showToastNotification) {
                                                    showToastNotification('Failed to generate social post. Please try again.', 'error');
                                                }
                                            } finally {
                                                setGeneratingSocialPost(false);
                                            }
                                        }}
                                        style={{
                                            padding: '5px 12px',
                                            background: 'none',
                                            color: '#3C4A5E',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: generatingSocialPost ? 'wait' : 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                            transition: 'none',
                                            textTransform: 'capitalize',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            opacity: generatingSocialPost ? 0.6 : 1,
                                        }}
                                    >
                                        <ShareIcon size={12} />
                                        {generatingSocialPost ? 'Generating...' : 'Create Social Post'}
                                    </button>
                                )}
                            </div>
                            {/* Article count on right */}
                            {shouldShowStoryCardArticleCount && (
                                <div style={{fontWeight: 'bold', fontSize: '13px', color: '#555', whiteSpace: 'nowrap', flexShrink: 0}}>
                                    {storyCardArticleCount} {storyCardArticleCount === 1 ? 'article' : 'articles'}
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Preview content - shown when expanded */}
                    {article.excerpt && isExpanded && (
                        <div style={{ marginTop: '6px' }}>
                            <div style={{
                                padding: '10px',
                                paddingRight: '35px',
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                borderRadius: '6px',
                                borderLeft: '3px solid #0066cc',
                                fontSize: '13px',
                                lineHeight: '1.5',
                                color: '#0B1E3F',
                                fontWeight: '400',
                                minHeight: isTranslating ? '60px' : 'auto',
                                position: 'relative'
                            }}>
                                {!article.excerpt?.isEnglish && !isTranslating && translatedExcerpt && (
                                    <span style={{ fontSize: '10px', color: '#888', display: 'block', marginBottom: '4px' }}>
                                        (translated)
                                    </span>
                                )}
                                    {/* Floating copy icon */}
                                    {!isTranslating && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const textToCopy = translatedExcerpt || getExcerptText(article.excerpt);
                                                navigator.clipboard.writeText(textToCopy)
                                                    .then(() => {
                                                        if (showToastNotification) {
                                                            showToastNotification('Preview copied to clipboard!');
                                                        }
                                                    })
                                                    .catch(err => {
                                                        console.error('Failed to copy:', err);
                                                        if (showToastNotification) {
                                                            showToastNotification('Failed to copy preview', 'error');
                                                        }
                                                    });
                                            }}
                                            style={{
                                                position: 'absolute',
                                                top: '8px',
                                                right: '8px',
                                                background: 'rgba(255, 255, 255, 0.8)',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                padding: '4px 6px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                opacity: 0.7,
                                                transition: 'opacity 0.2s',
                                                color: '#555'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                                            title="Copy preview"
                                        >
                                            <CopyIcon size={14} />
                                        </button>
                                    )}
                                {isTranslating ? (
                                    <SkeletonLoader />
                                ) : (
                                    `"${translatedExcerpt || getExcerptText(article.excerpt)}"`
                                )}
                            </div>
                        </div>
                    )}
                    {prs && isAdmin && type === 'article' && article.image &&
                        (<div>
                        <img src={article.image} alt="Descriptive Alt Text" style={{maxWidth: '100%', height: 'auto'}}/>
                    </div>)}
                    {/* Action buttons for all articles - Copy Link */}
                    {type === 'article' && !article?.hidden && (
                        <div className="article-actions-row" style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginTop: '6px', gap: '8px'}}>
                            {/* Copy Link button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const storyTitle = article.storyTitle || '';
                                    const articleHeadline = article.subject || article.title || '';
                                    const articleUrl = article.url || '';
                                    const textToCopy = `${storyTitle}\n${articleHeadline}\n${articleUrl}`;
                                    navigator.clipboard.writeText(textToCopy)
                                        .then(() => {
                                            if (showToastNotification) {
                                                showToastNotification('Link copied to clipboard!');
                                            }
                                        })
                                        .catch(err => {
                                            console.error('Failed to copy:', err);
                                            if (showToastNotification) {
                                                showToastNotification('Failed to copy link', 'error');
                                            }
                                        });
                                }}
                                style={{
                                    padding: '2px 0',
                                    background: 'none',
                                    color: '#00AEEF',
                                    border: 'none',
                                    borderRadius: '0',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '400',
                                    boxShadow: 'none',
                                    transition: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}
                            >
                                <CopyIcon size={12} /> Copy Link
                            </button>
                            {/* Remove link */}
                            {canEdit && (
                                <button
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        await hideArticle(article._id);
                                    }}
                                    style={{
                                        padding: '5px 12px',
                                        background: 'none',
                                        color: '#FF6B35',
                                        border: 'none',
                                        borderRadius: '0',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '400',
                                        boxShadow: 'none',
                                        transition: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
                                    </svg>
                                    Remove
                                </button>
                            )}
                        </div>
                    )}
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default React.memo(Article);
